import { computed } from 'vue';
import type { ColumnDef } from '@tanstack/vue-table';
import { locationLabel } from '@/composables/records/recordsFormat';
import { BLANK_FACET_VALUE, type RecordFacetDefinitions } from '@/composables/records/useRecordsAggregation';
import { useRecordsPage } from '@/composables/records/useRecordsPage';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import { queryKeys, useDeaths } from '@/plugins/store/actions/queries/deaths';
import { RECORD_ACTIONS_COLUMN_ID, type DeathRow } from '@/types';
import { TOGGLE_COLUMN_IDS, type DeathsToggleColumnId } from './useDeathsColumnVisibility';

const HEADING_KEYS: Record<DeathsToggleColumnId, string> = {
    created_by: 'views.pigs.records.table.headings.createdBy',
};

/** The bulk mortality offered above the table: one dead registration on a barn divided into its sections (the dead form, in bulk). */
export const BULK_CREATE = {
    formId: 6,
    labelKey: 'views.pigs.deathsNew.table.createButton',
} as const;

/**
 * What the Deaths page filters on: the one location a registration happened at, and the three facts
 * that describe it. A death carries no `from_*` chain - see `DeathRow` - so unlike Insertions and
 * Transfers there is no blank-fallback case for a location chain here: the farm/barn always resolve.
 *
 * `to_section`, `cause` and `category` do fall back to the blank option, the same as Insertions' and
 * Transfers' own nullable facets: a section is absent whenever the registration was made at or above
 * Barn level (or on a Batch under a Barn rather than a Section), and `cause`/`category` are absent
 * whenever no cause was registered. `deadoreuthanized` is always populated - it is sourced off a
 * required foreign key - so it carries no fallback.
 */
function facets(): RecordFacetDefinitions<DeathRow> {
    const blank = i18n.global.t('views.pigs.records.table.texts.blank');

    return {
        to_barn: {
            value: (row) => locationLabel(row.to_farm ?? null, row.to_barn ?? null),
            label: (row) => locationLabel(row.to_farm ?? null, row.to_barn ?? null),
        },
        to_section: {
            value: (row) => row.to_section?.id ?? BLANK_FACET_VALUE,
            label: (row) => row.to_section?.name ?? blank,
        },
        deadoreuthanized: {
            value: (row) => row.deadoreuthanized,
            label: (row) => row.deadoreuthanized,
        },
        cause: {
            value: (row) => row.cause ?? BLANK_FACET_VALUE,
            label: (row) => row.cause ?? blank,
        },
        category: {
            value: (row) => row.category ?? BLANK_FACET_VALUE,
            label: (row) => row.category ?? blank,
        },
    };
}

/**
 * The columns, in the order a mortality registration reads: when it happened, where, how many pigs
 * and at what weight, and the three facts describing the death.
 *
 * `to_barn`/`to_section`/`number`/`avg_weight`/`total_weight` reuse the same column ids Insertions
 * and Transfers render under - see `DeathRow` for why - with their own headings here since this page
 * names them "Location"/"Section" rather than "To location"/"To section".
 *
 * `to_section` and `category` are each dropped entirely when nothing in the full loaded set carries
 * one: most solutions never register a death below Barn level, and category is resolved only when
 * a cause with one was picked. A column that is blank on every row is noise rather than information.
 */
function columns(rows: DeathRow[], isVisible: (columnId: DeathsToggleColumnId) => boolean): ColumnDef<DeathRow>[] {
    const hasSection = rows.some((row) => row.to_section !== null);
    const hasCategory = rows.some((row) => row.category !== null);

    return [
        { id: 'date', accessorKey: 'date', header: i18n.global.t('views.pigs.records.table.headings.date'), enableSorting: true },
        { id: 'to_barn', header: i18n.global.t('views.pigs.records.table.headings.location'), meta: { filterable: true } },
        ...(hasSection ? [{ id: 'to_section', header: i18n.global.t('views.pigs.records.table.headings.section'), meta: { filterable: true } } satisfies ColumnDef<DeathRow>] : []),
        { id: 'number', accessorKey: 'number', header: i18n.global.t('views.pigs.records.table.headings.number'), enableSorting: true, meta: { align: 'right' } },
        { id: 'avg_weight', accessorKey: 'avg_weight', header: i18n.global.t('views.pigs.records.table.headings.avgWeight'), enableSorting: true, meta: { align: 'right' } },
        { id: 'total_weight', accessorKey: 'total_weight', header: i18n.global.t('views.pigs.records.table.headings.totalWeight'), enableSorting: true, meta: { align: 'right' } },
        { id: 'deadoreuthanized', header: i18n.global.t('views.pigs.records.table.headings.deadOrEuthanized'), meta: { filterable: true } },
        { id: 'cause', header: i18n.global.t('views.pigs.records.table.headings.cause'), meta: { filterable: true } },
        ...(hasCategory ? [{ id: 'category', header: i18n.global.t('views.pigs.records.table.headings.category'), meta: { filterable: true } } satisfies ColumnDef<DeathRow>] : []),
        ...(isVisible('created_by') ? [{ id: 'created_by', accessorKey: 'created_by', header: i18n.global.t(HEADING_KEYS.created_by), enableSorting: true } satisfies ColumnDef<DeathRow>] : []),
        { id: RECORD_ACTIONS_COLUMN_ID, header: '', meta: { align: 'right' } },
    ];
}

/** The Deaths page's configuration of the shared records page. */
export function useDeathsPage() {
    const page = useRecordsPage<DeathRow>({
        records: { use: useDeaths, rootKey: queryKeys.getDeathsRoot },
        facets,
    });

    // Created by is always offered and off by default.
    const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<DeathsToggleColumnId>({
        storageKey: 'pigs.deaths.columnVisibility',
        columnIds: TOGGLE_COLUMN_IDS,
        isAvailable: () => true,
        defaultVisible: () => false,
        label: (id) => i18n.global.t(HEADING_KEYS[id]),
    });

    // Computed rather than a constant so the headings follow a language switch and the Section
    // column follows the loaded data.
    return { ...page, columns: computed(() => columns(page.rows.value, isVisible)), columnToggleOptions, toggleColumn, setAllColumns };
}
